'use client';

import { redirect, RedirectType } from 'next/navigation';

import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import classNames from 'classnames';
import Cookies from 'js-cookie';
import { debounce } from 'lodash';
import { useInterval } from 'usehooks-ts';
import { Address } from 'viem';

import {
  useGetOneInchGasPricesQuery,
  useGetOneInchNetworksQuery,
  useGetOneInchQuoteQuery,
  useGetOneInchTokenAllowanceQuery,
  useGetOneInchTokensQuery,
} from '@query';

import { OneInchNetwork, OneInchToken } from '@api';

import Card from '@uikit/components/Card/Card';
import { useModalController } from '@uikit/components/Modal';

import { MIXPANEL_EVENT_NAMES } from '@shared/api/mixPanel';
import { useTrackMixPanel } from '@shared/common/providers/MixPanelProvider';
import storageKeys from '@shared/constants/storageKeys';
import displayWalletErrorToast from '@shared/helpers/displayWalletErrorToast';
import { numberToWei } from '@shared/helpers/web3/formatters/numberToWei';
import { initZeroTokenBalance, TokenBalance } from '@shared/helpers/web3/getUserTokenBalance';
import useWeb3 from '@shared/helpers/web3/hooks/useWeb3';
import useGetSwapperTerms from '@shared/hooks/useGetSwapperTerms';
import useMagicIdWallet from '@shared/hooks/useMagicIdWallet';

import useSwitchNetworkUtil from '@magic-swap/components/pages/MagicSwap/hooks/useSwitchNetworkUtil';

import { DEFAULT_QUOTE_REFETCH_INTERVAL } from './constants';
import useSwapTokenBalance from './hooks/useSwapTokenBalance';
import useTokenPreselection from './hooks/useTokenPreselection';
import { OneInchView, SwapState } from './types';
import { OneInchTermsModal } from './ui/modals/OneInchTermsModal';
import MagicSwapFailureView from './ui/views/MagicSwapFailureView';
import MagicSwapMainView from './ui/views/MagicSwapMainView';
import MagicSwapNetworkSelectView from './ui/views/MagicSwapNetworkSelectView';
import MagicSwapSuccessView, { SuccessSwapTxInfo } from './ui/views/MagicSwapSuccessView';
import MagicSwapTokenSelectView from './ui/views/MagicSwapTokenSelectView';

// TODO: fix many renders
const MagicSwapPage = () => {
  const termsAndConditionsModal = useModalController(
    !Cookies.get(storageKeys.SELF_SWAP_TERMS_AGREEMENT),
  );

  const { web3Account } = useWeb3();
  const { user, userWallet } = useMagicIdWallet();
  const { switchNetworkUtil } = useSwitchNetworkUtil();
  const { showSwapper, isLoadingTerms } = useGetSwapperTerms();

  useTrackMixPanel(MIXPANEL_EVENT_NAMES.SWAP_PAGE_VIEW, {
    distinct_id: user?.id as number,
  });

  const [view, setView] = useState<OneInchView>('main');
  const [network, setNetwork] = useState<OneInchNetwork>();
  const [fromToken, setFromToken] = useState<OneInchToken>();
  const [fromTokenBalance, setFromTokenBalance] = useState<TokenBalance>(initZeroTokenBalance());
  const [toToken, setToToken] = useState<OneInchToken>();
  const [toTokenBalance, setToTokenBalance] = useState<TokenBalance>(initZeroTokenBalance());
  const [stringFromAmount, setFromAmount] = useState('');
  const fromAmount = Number(stringFromAmount || 0);
  const [slippage, setSlippage] = useState(1);
  const [successTxInfo, setSuccessTxInfo] = useState<SuccessSwapTxInfo>();
  const [txError, setTxError] = useState<Error>();
  const [swapState, setSwapState] = useState<SwapState>({
    isUsingMax: false,
    isSwapping: false,
    maxAmountRefetchInterval: 0,
    quoteRefetchInterval: 0,
  });

  const { data: networks = [], isFetching: isLoadingNetwork } = useGetOneInchNetworksQuery();
  const {
    data: tokens = [],
    isFetching: isLoadingTokens,
    refetch: fetchTokens,
  } = useGetOneInchTokensQuery({
    networkId: network?.id,
    options: { enabled: false },
  });

  const {
    data: quote,
    isFetching: isLoadingQuote,
    refetch: fetchQuote,
  } = useGetOneInchQuoteQuery({
    apiOptions:
      !network || !fromToken || !toToken
        ? undefined
        : {
            searchParams: {
              networkId: network.id,
              fromTokenAddress: fromToken.id,
              toTokenAddress: toToken.id,
              amount: String(numberToWei(fromAmount, fromToken.attributes.decimal)),
            },
          },
    options: { enabled: false },
  });
  const allowanceQuery = useGetOneInchTokenAllowanceQuery({
    networkId: network?.id,
    apiOptions:
      !fromToken || !web3Account?.address
        ? undefined
        : {
            searchParams: {
              tokenAddress: fromToken.id as Address,
              walletAddress: web3Account.address,
            },
          },
    options: { enabled: false },
  });
  const { refetch: fetchAllowance } = allowanceQuery;

  const { data: gasPrices, refetch: fetchGasPrice } = useGetOneInchGasPricesQuery({
    networkId: network?.id,
    options: { enabled: !!network?.id },
  });

  const { tokenToReceive, tokenToSell } = useTokenPreselection({
    network,
    networks,
    tokens,
    setNetwork,
    setTokenFrom: setFromToken,
    setTokenTo: setToToken,
    isLoading: isLoadingNetwork || isLoadingTokens,
  });

  useEffect(() => {
    if (!isLoadingNetwork && network?.id) {
      fetchTokens();
      fetchGasPrice();
    }
  }, [network?.id, isLoadingNetwork, fetchTokens]);

  const { refetch: fetchFromTokenBalance } = useSwapTokenBalance({
    address: userWallet?.attributes.address,
    network,
    token: fromToken,
    setTokenBalance: setFromTokenBalance,
  });
  const { refetch: fetchToTokenBalance } = useSwapTokenBalance({
    address: userWallet?.attributes.address,
    network,
    token: toToken,
    setTokenBalance: setToTokenBalance,
  });

  const isAbleToLoadQuote = Boolean(
    !isLoadingNetwork &&
      !isLoadingTokens &&
      network?.id &&
      fromToken?.id &&
      toToken?.id &&
      fromAmount,
  );

  const isSelfWalletConnected = useMemo(
    () => web3Account?.address?.toLowerCase() === userWallet?.attributes?.address?.toLowerCase(),
    [web3Account?.address, userWallet?.attributes?.address],
  );

  const refetchQuote = debounce(async () => {
    if (!isAbleToLoadQuote) {
      return;
    }

    await fetchQuote();

    if (isSelfWalletConnected) {
      await fetchAllowance();
    }

    setSwapState((prev) => ({ ...prev, quoteRefetchInterval: DEFAULT_QUOTE_REFETCH_INTERVAL }));
  }, 1400);

  useEffect(() => {
    refetchQuote();
  }, [network, fromToken, toToken, fromAmount]);

  useInterval(
    refetchQuote,
    !swapState.isSwapping && isAbleToLoadQuote && swapState.quoteRefetchInterval && fromAmount
      ? swapState.quoteRefetchInterval
      : null,
  );

  const handleChangeNetwork = async (selectedNetwork: OneInchNetwork) => {
    try {
      if (isSelfWalletConnected) {
        await switchNetworkUtil({
          networkId: selectedNetwork.id,
          terminateConnectionOnError: false,
        });
      }

      setFromAmount('');
      setNetwork(selectedNetwork);
      setFromToken(undefined);
      setToToken(undefined);
      refetchQuote();
    } catch (error) {
      displayWalletErrorToast({ error });
    }
  };

  const setToken = (type: 'to' | 'from', token?: OneInchToken, isInvertAction = false) => {
    if (!token) {
      return;
    }

    if (type === 'from') {
      if (token.id === toToken?.id && !isInvertAction) {
        setToToken(undefined);
      }

      setFromToken(token);
    } else {
      if (token.id === fromToken?.id && !isInvertAction) {
        setFromToken(undefined);
      }

      setToToken(token);
    }

    refetchQuote();
  };

  const createNewSwap = () => {
    setFromToken(tokenToSell);
    setToToken(tokenToReceive);
    setFromAmount('');
    setSlippage(1);
    setView('main');
    fetchFromTokenBalance().then();
    fetchToTokenBalance().then();
  };

  // NOTE #g21b78g23h788: FT Checks in HydratedPade.tsx and redirects to 404 page,
  // so we don't need to check exactly terms here
  if (!showSwapper && !isLoadingTerms) {
    const message =
      'Your IP address indicates that you are attempting to access swap service from a restricted jurisdiction. As per our Terms of Use, we are unable to provide services to users from restricted jurisdictions.';

    toast.info(message, { toastId: message, autoClose: 30000 });
    redirect('/', RedirectType.replace);
  }

  return (
    <>
      <div
        className={classNames(
          'flex flex-col v2-container h-full justify-center items-center py-[50px]',
        )}
      >
        <Card className="w-full max-w-[700px] sm:min-h-[560px] rounded-3xl !py-[24px] !px-[28px] max-sm:!px-[8px] flex flex-col items-center">
          {view === 'main' && (
            <MagicSwapMainView
              allowanceQuery={allowanceQuery}
              isLoadingQuote={isLoadingQuote}
              quote={quote}
              fromAmount={fromAmount}
              refetchQuote={refetchQuote as () => Promise<void>}
              slippage={slippage}
              setSlippage={setSlippage}
              setSuccessTxInfo={setSuccessTxInfo}
              setTxError={setTxError}
              setView={setView}
              network={network}
              fromToken={fromToken}
              toToken={toToken}
              stringFromAmount={stringFromAmount}
              isLoadingNetwork={isLoadingNetwork}
              isLoadingTokens={isLoadingTokens}
              fromTokenBalance={fromTokenBalance}
              setFromAmount={setFromAmount}
              setFromToken={(...args) => setToken('from', ...args)}
              setToToken={(...args) => setToken('to', ...args)}
              fetchFromTokenBalance={fetchFromTokenBalance}
              fetchToTokenBalance={fetchToTokenBalance}
              tokens={tokens}
              toTokenBalance={toTokenBalance}
              swapState={swapState}
              setSwapState={setSwapState}
              gasPrices={gasPrices}
            />
          )}

          {view === 'networkSelect' && (
            <MagicSwapNetworkSelectView
              networks={networks || []}
              setNetwork={handleChangeNetwork}
              setView={setView}
            />
          )}

          {view === 'fromTokenSelect' && (
            <MagicSwapTokenSelectView
              tokens={tokens || []}
              setToken={(token) => setToken('from', token)}
              setView={setView}
              title="You Pay"
              fetchTokenBalance={fetchFromTokenBalance}
            />
          )}

          {view === 'toTokenSelect' && (
            <MagicSwapTokenSelectView
              tokens={tokens || []}
              setToken={(token) => setToken('to', token)}
              setView={setView}
              title="You Receive"
              fetchTokenBalance={fetchToTokenBalance}
            />
          )}

          {view === 'success' && (
            <MagicSwapSuccessView successTxInfo={successTxInfo} createNewSwap={createNewSwap} />
          )}

          {view === 'failure' && (
            <MagicSwapFailureView txError={txError} createNewSwap={createNewSwap} />
          )}
        </Card>
      </div>

      <OneInchTermsModal modalController={termsAndConditionsModal} />
    </>
  );
};

export default MagicSwapPage;
